import React from "react";
import Squire from "squire-rte";
import { readAndCompressImage } from "browser-image-resizer";
import validateURL from "url-validator";
import { Icon, close } from "Icons/index";
import TextArea from "Components/textArea/TextArea";
import Button from "Components/button/Button";
import Input from "Components/input/Input";
import Toolbar from "./Toolbar";
import cx from "classnames";
import s from "./richEditor.scss";
import { bytesToHuman } from "@/utils/bytesFormats";
import { t } from "i18next";

const strike = (editor) => {
  return () => {
    editor.changeFormat({ tag: "DEL" });
  };
};

const removeStrike = (editor) => {
  return () => {
    editor.changeFormat(null, { tag: "DEL" });
  };
};

const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export default class Editor extends React.Component {
  state = {
    inlineImages: {},
    plainTextMode: false,
    editorInitialized: false,
    urlEditOpened: false,
    // url dialog
    urlEditUrl: "",
    urlEditText: "",
    urlEditNode: undefined,
    urlEditMode: "", // create, create-from-selection, edit
    // html dialog
    rawHTMLDialogOpened: false,
    rawHTMLContent: "",
    // image dialog
    imageDialogOpened: false,
    pendingImage: false,
  };

  // urlEditFieldRef = React.createRef();
  editorInitialNodeRef = React.createRef();

  scrollEditorBody = (offset) => {
    const editorBody = document.getElementById("editor-body");
    editorBody.scrollTo({
      top: editorBody.scrollTop + offset,
      behavior: "smooth",
    });
  };

  handleCursorMoved = () => {
    if (!this.toolbarPosition) return false;
    const cursorPosition = this.editor && this.editor.getCursorPosition();

    // follow the cursor with scroll
    if (this.toolbarPosition.top - 120 < cursorPosition.top) {
      this.scrollEditorBody(60);
    } else if (cursorPosition.top <= this.editorBodyPosition.top + 40) {
      this.scrollEditorBody(-60);
    }
  };

  componentDidMount() {
    const { initialValue, initialFocusPosition, id } = this.props;

    const editor = new Squire(this.editorInitialNodeRef.current, {
      blockTag: "DIV",
      blockAttributes: { style: "color: black;font-size: 14px;" }, // this is necessary because Gmail convert it to #50050 if text is the same: https://litmus.com/community/discussions/5189-gmail-changes-color-of-text-im-class
    });

    /* extend editor functionalities */
    editor.strike = strike(editor);
    editor.removeStrike = removeStrike(editor);

    if (this.props.onFocus) {
      editor.addEventListener("focus", this.props.onFocus);
    }
    editor.addEventListener("cursor", this.handleCursorMoved);

    editor.addEventListener("willPaste", this.handleWillPaste);

    document.getElementById(`editor-body`).addEventListener("paste", this.clipboardHandler);

    if (this.props.onChange) {
      editor.addEventListener("input", this.props.onChange);
    }

    this.editor = editor;

    // Initial value is read and set on the editor
    // only here, from now on this prop will be ignored to
    // not break the focus of the editor while is used
    if (initialValue) {
      editor.setHTML(initialValue);
    }

    const editorBody = document.getElementById("editor-body");
    editorBody.addEventListener("click", (e) => {
      if (e.target.nodeName === "BLOCKQUOTE") {
        // let text1 = document.createTextNode("1");
        // let li = document.createElement("li");
        // li.appendChild(text1);
        // e.target.insertBefore(li, ul.firstChild);
      }

      if (e.target.nodeName === "A" || e.target.closest("a")) {
        let aNode = e.target.nodeName === "A" ? e.target : e.target.closest("a");

        this.showUrlEditDialog(e.target.href, e.target.innerText, "edit", aNode);
      }

      if (e.target.nodeName === "IMG") {
        this.selectImage(e);
      }
    });

    // When user select "edit as new" on a received message
    // the editor must focus at the end of the message
    if (initialFocusPosition === "end") {
      editor.moveCursorToEnd();
      // let signature = this.node.querySelector(".signature");

      // if (!signature) {
      // } else {
      //   let range = document.createRange();
      //   let lastBodyParagraph = signature.previousSibling;
      //   range.setStartBefore(lastBodyParagraph);
      //   editor.setSelection(range);
      // }
    } else if (initialFocusPosition === "start") {
      editor.moveCursorToStart();
    }
    // this parameters is used only to force a re-render after didMount fired
    // so the whole component get "aware" of the this.editor value
    this.setState({ editorInitialized: true }, () => {
      // cache toolbar container
      this.toolbarPosition = document.getElementById("toolbarAdvanced")?.getBoundingClientRect();
      this.editorBodyPosition = document.getElementById("editor-body")?.getBoundingClientRect();
    });

    if (this.props.autoFocus) {
      this.editorInitialNodeRef.current.focus();
    }

    // handle file input
    this.fileInput.addEventListener("change", this.handleImageUpload);
  }

  handleImageUpload = (e) => {
    const { maxImageBytes } = this.props;
    const file = e.target.files[0];

    if (maxImageBytes && file.size > maxImageBytes) {
      window.alert(t("signature.maxImageSize", { size: bytesToHuman(file.size) }) + bytesToHuman(maxImageBytes));
      return;
    }

    if (e.target.files[0]) {
      this.addInlineImage(e.target.files[0]);
    }

    // reset file input state
    this.fileInput.value = "";
  };

  pasteImages = (event) => {
    for (let item of event.clipboardData.itemo) {
      if (item.type.indexOf("image") === -1) continue;
      const blob = item.getAsFile();
      this.addInlineImage(blob);
    }
  };

  getContent = () => {
    let html = this.editor.getHTML() || "";

    // remove <br> from <colspan> tags that breaks layout on receiving clien
    html = html.replace(/<br><\/colgroup>/g, "</colgroup>");

    return {
      html: html,
      text: "", //this.getPlainTextVersion(),
    };
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.plainTextMode && this.props.plainTextMode) {
      this.getPlainTextVersion((text) => {
        if (text) {
          text = text.replace(/[\r\n]/g, "<br>");
          this.setContent(text);
        }
      });
    }
  }

  // this event can be used to cancel a paste event and execute custom logic
  handleWillPaste = (e) => {
    if (this.cancelPaste) {
      e.preventDefault(); // cancel the past actions
      this.cancelPaste = null;
    }
  };

  clipboardHandler = (e) => {
    // return;
    let hasImage = false;
    let hasRTF = false;
    let hasHTML = false;
    // let hasPlainText = false;

    let plainTextItem;
    for (let item of e.clipboardData.items) {
      const type = item.type;
      if (type === "text/html") {
        hasHTML = true;
        // iOS copy URL gives you type text/uri-list which is just a list
        // of 1 or more URLs separated by new lines. Can just treat as
        // plain text.
      } else if (type === "text/plain" || type === "text/uri-list") {
        plainTextItem = item;
        // hasPlainText = true;
      } else if (type === "text/rtf") {
        hasRTF = true;
      } else if (/^image\/.*/.test(type)) {
        hasImage = true;
      }

      // uncomment to debug clipload content
      // item.getAsString((text) => {
      //   console.log("Clipboard Item:", text);
      // });
    }

    // if can only copy
    if (hasHTML && this.props.pasteOnlyText && plainTextItem) {
      this.cancelPaste = true;
      plainTextItem.getAsString((text) => this.editor.insertHTML(text));
    }

    if (hasHTML && hasImage && !(hasRTF && hasHTML)) {
      // when you copy in Excel, you get html, rtf, text, image;
      this.pasteImages(e);
    }
  };

  getPlainTextVersion = (cb) => {
    const htmlContent = this.editor.getHTML();

    let xhr = new XMLHttpRequest();
    xhr.open("POST", env.be_url + "/imap/plaintext_version", true);
    xhr.setRequestHeader("Content-type", "application/json");
    xhr.setRequestHeader("x-access-token", sessionStore.token);
    xhr.send(
      JSON.stringify({
        html: htmlContent,
      }),
    );

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        let response = JSON.parse(xhr.response);
        if (xhr.status === 200) {
          cb(response.text);
        } else {
          // error management
          cb("");
        }
      }
    };
  };

  // shouldComponentUpdate(nextProps, nextState) {
  //   //let changedPlainText = this.props.plainTextMode !== nextProps.plainTextMode;

  //   // console.log("debug shouldComponentUpdate", changedInitialValue, changedPlainText);

  //   // if (changedPlainText) {
  //   //   return true;
  //   // }
  //   return true;
  // }

  setContent = (content, append = false) => {
    if (!content) return null;

    if (append) {
      this.editor.insertHTML(content);
    } else {
      this.editor.setHTML(content);
    }
  };

  callMethodOnSquire(method) {
    const args = Array.prototype.slice.call(arguments);
    const squireArgs = args.slice(1);
    return this.editor[method](...squireArgs);
  }

  clearContent = () => {
    this.editor.setHTML("");
  };

  componentWillUnmount() {
    if (this.props.onFocus) {
      this.editor.removeEventListener("focus", this.props.onFocus);
    }

    this.editor.removeEventListener("cursor", this.handleCursorMoved);

    document.getElementById("editor-body").removeEventListener("paste", this.clipboardHandler);

    this.editor.removeEventListener("willPaste", this.handleWillPaste);

    if (this.props.onChange) {
      this.editor.removeEventListener("input", this.props.onChange);
    }

    this.editor.destroy();
    this.setState({ editor: null });
  }

  checkImageSize = (file) => {
    return new Promise((resolve, reject) => {
      let img = new Image();
      img.src = window.URL.createObjectURL(file);
      img.onload = () => resolve({ width: img.width, height: img.height });

      img.onerror = (e) => reject(e);
    });
  };

  resizeImage = async (file) => {
    // const dimensions = await this.checkImageSize(file);
    // if (dimensions.width < 650) return file;
    // console.log("dimensions", dimensions);

    // let maxWidth = 650;
    // let maxHeight = (dimensions.height * maxWidth) / dimensions.width;
    // maxHeight = maxHeight.toFixed(2);

    const resizedImage = await readAndCompressImage(file, {
      quality: 0.9,
      maxWidth: 1200,
      maxHeight: 900,
      autoRotate: true,
      debug: true,
    });

    return resizedImage;
  };

  addInlineImage = async (file) => {
    const resizedImage = await this.resizeImage(file);

    let b64 = await toBase64(resizedImage);
    const ids = Object.keys(this.state.inlineImages);
    let counter = parseInt(ids.length, 10);

    let newCounter = counter + 1;
    const updated = {
      ...this.state.inlineImages,
      [newCounter]: b64,
    };

    // clean removed images
    ids.forEach((i) => {
      if (!document.getElementById("inline-image-" + i)) {
        delete updated[i];
      }
    });

    this.setState({
      inlineImages: updated,
    });

    console.log("b64", new TextEncoder().encode(b64).length);

    this.editor.insertImage(b64, {
      class: "editor-inline-image",
      id: "inline-image-" + newCounter,
      "data-filename": file.name,
      "data-filesize": resizedImage.size,
    });

    document.getElementById("inline-image-" + newCounter).onclick = this.selectImage;

    // go to new line
    this.editor.insertHTML("<div></div><div></div>");
  };

  selectImage = (e) => {
    let img = e.target;

    let range = document.createRange();
    range.setStartBefore(img);
    range.setEndAfter(img);

    this.editor.setSelection(range);
  };

  onAddImage = () => this.fileInput.click();

  /* url dialog methods */
  showUrlEditDialog = (url, text, mode = "create", domNode = null) => {
    this.setState(
      {
        urlEditOpened: true,
        urlEditUrl: url,
        urlEditText: text,
        urlEditMode: mode,
        urlEditNode: domNode,
      },
      () => {
        // this.urlEditFieldRef.current.focus();
      },
    );
  };

  handleUrlChange = (val) => {
    this.setState({ urlEditUrl: val });
  };

  handleUrlTextChange = (val) => {
    this.setState({ urlEditText: val });
  };

  removeUrl = () => {
    this.callMethodOnSquire("removeLink");
    this.closeUrlEditDialog();
  };

  insertUrlHTML = () => {
    if (!validateURL(this.state.urlEditUrl)) {
      alert("URL non valido!");
      return;
    }

    if (this.state.urlEditMode === "create") {
      // create temporary node to give the text of the link
      let range = document.createRange();

      this.editor.insertHTML('<a id="temp_qb_node_122324094039">' + this.state.urlEditText + " </a>");

      let newNode = document.getElementById("temp_qb_node_122324094039");
      range.selectNode(newNode);
      this.callMethodOnSquire("setSelection", range);

      this.callMethodOnSquire("makeLink", this.state.urlEditUrl, {
        target: "_blank",
        class: "editor-link",
      });

      // set cursor to the end of the link just created
      const currentRange = this.callMethodOnSquire("getSelection");
      currentRange.collapse();
      this.callMethodOnSquire("setSelection", currentRange);
    } else if (this.state.urlEditMode === "edit") {
      const urlEditNode = this.state.urlEditNode;
      urlEditNode.innerText = this.state.urlEditText;
      urlEditNode.href = this.state.urlEditUrl;
    } else if (this.state.urlEditMode === "create-from-selection") {
      this.callMethodOnSquire("makeLink", this.state.urlEditUrl, {
        target: "_blank",
        class: "editor-link",
      });
    }

    this.closeUrlEditDialog();
  };

  closeUrlEditDialog = () => {
    this.setState({
      urlEditOpened: false,
      urlEditUrl: "",
      urlEditText: "",
      urlEditNode: undefined,
    });
  };

  /* html dialog methods */
  showRawHTMLDialog = () => {
    this.setState({
      rawHTMLDialogOpened: true,
      rawHTMLContent: this.getContent().html,
    });
  };

  closeRawHTMLDialog = () => {
    this.setState({ rawHTMLDialogOpened: false });
  };

  handleRawHTMLChange = (val) => {
    this.setState({ rawHTMLContent: val });
  };

  insertRawHTML = () => {
    this.setContent(this.state.rawHTMLContent);
    this.setState({ rawHTMLDialogOpened: false, rawHTMLContent: "" });

    if (this.props.onChange) this.props.onChange();
  };

  render() {
    const { className, label, disabled, id } = this.props;

    return (
      <div className={cx({ [s.disabled]: disabled })}>
        {label && (
          <div className={s.wrapLabel}>
            <label className={s.label}>{label}</label>
          </div>
        )}
        <div id={`editor${id}`} className={cx(s.richEditor, className, "richEditorQboxmail")}>
          <div id="editor-body" className={s.editor} ref={this.editorInitialNodeRef} />

          {this.editor && (
            <Toolbar
              id={id}
              className={s.toolbar}
              onAddImage={this.onAddImage}
              showRawHTMLDialog={this.showRawHTMLDialog}
              editor={this.editor}
              showUrlEditDialog={this.showUrlEditDialog}
            />
          )}

          <input
            ref={(el) => (this.fileInput = el)}
            accept="image/png, image/jpeg"
            type="file"
            style={{ display: "none" }}
          />

          {/* handle url */}
          {this.state.urlEditOpened && (
            <div className={s.editorModalOverlay}>
              <div className={s.editorModal}>
                <header className={s.editorModalHead}>
                  <h2 className={s.editorModalTitle}>Inserisci link</h2>
                  <Icon className={s.editorModalClose} svg={close} onClick={this.closeUrlEditDialog} />
                </header>

                <div className={s.editorModalBody}>
                  <Input
                    className={s.editorModalInput}
                    type="text"
                    name="url"
                    value={this.state.urlEditUrl}
                    onChange={(v) => this.handleUrlChange(v.url)}
                    label="Indirizzo Web"
                  />
                  <Input
                    className={s.editorModalInput}
                    type="text"
                    name="urlText"
                    value={this.state.urlEditText}
                    onChange={(v) => this.handleUrlTextChange(v.urlText)}
                    label="Testo visualizzato"
                  />
                </div>
                <footer className={s.editorModalFooter}>
                  {this.state.urlEditMode === "edit" && (
                    <Button onClick={() => this.removeUrl()} color="red-outline">
                      button.remove.link
                    </Button>
                  )}
                  <Button onClick={() => this.insertUrlHTML()}>button.insert</Button>
                </footer>
              </div>
            </div>
          )}

          {/* insert HTML raw */}
          {this.state.rawHTMLDialogOpened && (
            <div className={s.editorModalOverlay}>
              <div className={s.editorModal}>
                <header className={s.editorModalHead}>
                  <h2 className={s.editorModalTitle}>Inserisci codice HTML</h2>
                  <Icon className={s.editorModalClose} svg={close} onClick={this.closeRawHTMLDialog} />
                </header>

                <div className={s.editorModalBody}>
                  <TextArea
                    className={s.htmlEditor}
                    name="html"
                    value={this.state.rawHTMLContent}
                    onChange={(v) => this.handleRawHTMLChange(v.html)}
                    noLabel
                    required
                  />
                </div>
                <footer className={s.editorModalFooter}>
                  <Button onClick={this.insertRawHTML}>button.insert</Button>
                </footer>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
